import React from 'react';
import { Progress, Spin, Typography, Space, Card } from 'antd';
import { CheckCircleOutlined, LoadingOutlined, ClockCircleOutlined } from '@ant-design/icons';

const { Text } = Typography;

const ProgressIndicator = ({ 
  type = 'linear', // 'linear', 'circular', 'dots', 'steps'
  percent = 0,
  status = 'active', // 'active', 'success', 'exception', 'normal'
  showInfo = true,
  size = 'default', // 'small', 'default', 'large'
  title,
  description,
  steps = [],
  currentStep = 0,
  color = '#9333ea',
  strokeWidth = 8,
  style = {}
}) => {
  const renderLinearProgress = () => (
    <div style={{ width: '100%', ...style }}>
      {title && (
        <div style={{ marginBottom: 8 }}>
          <Text strong style={{ fontSize: 14, color: '#171717' }}>{title}</Text>
          {description && (
            <div style={{ marginTop: 2 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>{description}</Text>
            </div>
          )}
        </div>
      )}
      <Progress
        percent={percent}
        status={status}
        showInfo={showInfo}
        strokeColor={color}
        strokeWidth={strokeWidth}
        size={size}
        format={(percent) => (
          <span style={{ 
            fontSize: size === 'small' ? 11 : 12, 
            fontWeight: 175,
            color: status === 'exception' ? '#ef4444' : status === 'success' ? '#22c55e' : '#171717'
          }}>
            {percent}%
          </span>
        )}
      />
    </div>
  );

  const renderCircularProgress = () => (
    <div style={{ textAlign: 'center', ...style }}>
      {title && (
        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ fontSize: 14, color: '#171717' }}>{title}</Text>
          {description && (
            <div style={{ marginTop: 4 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>{description}</Text>
            </div>
          )}
        </div>
      )}
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <Progress
          type="circle"
          percent={percent}
          status={status}
          size={size === 'small' ? 60 : size === 'large' ? 120 : 80}
          strokeColor={color}
          strokeWidth={size === 'small' ? 6 : size === 'large' ? 10 : 8}
          format={(percent) => (
            <span style={{ 
              fontSize: size === 'small' ? 12 : size === 'large' ? 18 : 14, 
              fontWeight: 'bold',
              color: status === 'exception' ? '#ef4444' : status === 'success' ? '#22c55e' : '#171717'
            }}>
              {percent}%
            </span>
          )}
        />
        {status === 'active' && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)'
          }}>
            <LoadingOutlined style={{ fontSize: 16, color: color }} spin />
          </div>
        )}
      </div>
    </div>
  );

  const renderDotsProgress = () => (
    <div style={{ width: '100%', ...style }}>
      {title && (
        <div style={{ marginBottom: 12 }}>
          <Text strong style={{ fontSize: 14, color: '#171717' }}>{title}</Text>
          {description && (
            <div style={{ marginTop: 2 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>{description}</Text>
            </div>
          )}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {[...Array(5)].map((_, index) => {
          const isActive = index < Math.floor(percent / 20);
          const isCurrent = index === Math.floor(percent / 20);
          return (
            <div
              key={index}
              style={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                background: isActive ? color : '#e5e5e5',
                border: isCurrent ? `2px solid ${color}` : 'none',
                transition: 'all 0.3s ease',
                transform: isCurrent ? 'scale(1.2)' : 'scale(1)',
                boxShadow: isCurrent ? `0 0 8px ${color}40` : 'none'
              }}
            />
          );
        })}
        <Text style={{ fontSize: 12, color: '#737373', marginLeft: 8 }}>
          {percent}% complete
        </Text>
      </div>
    </div>
  );

  const renderStepsProgress = () => (
    <div style={{ width: '100%', ...style }}>
      {title && (
        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ fontSize: 14, color: '#171717' }}>{title}</Text>
          {description && (
            <div style={{ marginTop: 4 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>{description}</Text>
            </div>
          )}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {steps.map((step, index) => {
          const isActive = index === currentStep;
          const isCompleted = index < currentStep;
          const isPending = index > currentStep;
          
          return (
            <div key={index} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  background: isCompleted ? '#22c55e' : isActive ? color : '#e5e5e5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  border: isActive ? `2px solid ${color}` : 'none',
                  boxShadow: isActive ? `0 0 8px ${color}40` : 'none'
                }}
              >
                {isCompleted ? (
                  <CheckCircleOutlined style={{ color: 'white', fontSize: 12 }} />
                ) : isActive ? (
                  <LoadingOutlined style={{ color: 'white', fontSize: 12 }} spin />
                ) : (
                  <ClockCircleOutlined style={{ color: '#a3a3a3', fontSize: 12 }} />
                )}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: 13,
                  fontWeight: isActive ? 175 : 400,
                  color: isActive ? '#171717' : isCompleted ? '#737373' : '#a3a3a3'
                }}>
                  {step.title}
                </div>
                {step.description && (
                  <div style={{
                    fontSize: 11,
                    color: '#a3a3a3',
                    marginTop: 2
                  }}>
                    {step.description}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  switch (type) {
    case 'circular':
      return renderCircularProgress();
    case 'dots':
      return renderDotsProgress();
    case 'steps':
      return renderStepsProgress();
    case 'linear':
    default:
      return renderLinearProgress();
  }
};

// Predefined progress types for common operations
export const UploadProgress = ({ percent, filename }) => (
  <ProgressIndicator
    type="linear"
    percent={percent}
    title={`Uploading ${filename}`}
    description={`${percent}% uploaded`}
    color="#3b82f6"
    strokeWidth={6}
  />
);

export const ProcessingProgress = ({ percent, documentName }) => (
  <ProgressIndicator
    type="steps"
    percent={percent}
    currentStep={Math.floor(percent / 33)}
    steps={[
      { title: 'Analyzing document', description: 'Extracting text and metadata' },
      { title: 'Processing content', description: 'Identifying key information' },
      { title: 'Finalizing', description: 'Saving processed data' }
    ]}
    title={`Processing ${documentName}`}
    description="AI-powered document analysis in progress"
  />
);

export const DeleteProgress = ({ itemName }) => (
  <ProgressIndicator
    type="circular"
    percent={100}
    status="active"
    title={`Deleting ${itemName}`}
    description="Removing item permanently"
    color="#ef4444"
    size="small"
  />
);

export const CreateProgress = ({ itemName, percent }) => (
  <ProgressIndicator
    type="dots"
    percent={percent}
    title={`Creating ${itemName}`}
    description="Setting up new item"
    color="#22c55e"
  />
);

export default ProgressIndicator;
